import React, {
  createContext,
  ReactElement,
  ReactNode,
  useEffect,
  useRef,
  useState,
} from 'react';

import {
  MemoryRouter,
  NavigateOptions,
  useLocation,
  useNavigate,
  WindowLocation,
} from '..';

type THistory = {
  navigate: (to: string, options?: NavigateOptions) => Promise<void>;
  push: (to: string, options?: NavigateOptions) => Promise<void>;
  location: WindowLocation<object>;
};

type THistoryProps = {
  history: THistory;
  children: ReactNode | ReactElement | ReactElement[];
};

const useAsyncNavigate = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const resolveRef = useRef<() => void>();
  const [path, setPath] = useState<string | number | null>(null);

  const asyncNavigate = (to: string | number) => {
    setPath(to);
    return new Promise<void>((resolve) => {
      resolveRef.current = resolve;
    });
  };

  useEffect(() => {
    if (location.pathname === path) {
      if (resolveRef.current) {
        resolveRef.current();
        return;
      }
    }

    if (typeof path === 'string') {
      navigate(path as string);
    } else if (typeof path === 'number') {
      navigate(path as number);
    }

    // just to make sure we don't resolve the same navigation twice?
    return () => {
      if (resolveRef.current) {
        resolveRef.current();
      }
    };
  }, [location.pathname, navigate, path, resolveRef]);

  return { asyncNavigate };
};

const useHistory = (history: THistory): THistory => {
  const location = useLocation();
  const { asyncNavigate } = useAsyncNavigate();

  history.navigate = asyncNavigate;
  history.push = asyncNavigate;
  history.location = location;

  return history;
};

const HistoryContext = createContext({} as ReturnType<typeof useHistory>);

const HistoryProvider = ({
  history: historyParam, // we don't use history as param name, its just here for backwards compatibility
  children,
}: THistoryProps) => {
  const history = useHistory(historyParam);

  return (
    <HistoryContext.Provider value={history}>
      {children}
    </HistoryContext.Provider>
  );
};

export const HistoryLocationProvider = (props: THistoryProps) => {
  return (
    <MemoryRouter
      initialEntries={
        props.history.location.pathname
          ? [props.history.location.pathname]
          : undefined
      }>
      <HistoryProvider {...props} />
    </MemoryRouter>
  );
};

export const createHistory = (
  path: string | null,
): ReturnType<typeof useHistory> => {
  return {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    navigate: (to: string, options?: NavigateOptions) =>
      new Promise<void>((resolve) => {
        resolve();
      }),
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    push: (to: string, options?: NavigateOptions) =>
      new Promise<void>((resolve) => {
        resolve();
      }),
    location: {
      pathname: path,
      search: '',
    } as WindowLocation<object>,
  };
};

export const createMemorySource = (path: string): string => {
  return path;
};
